import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { useDxtradeAccountMetricsWS } from '@pages/Trade/Portfolio/context/DxtradeAccountMetricsContext'
import { LowMarginAlert } from '@pages/Trade/TradingAccountInfo/components/LowMarginAlert'
import { SelectedTradingAccount } from '@pages/Trade/TradingAccountInfo/components/SelectedTradingAccount'
import { TradingAccountInfoActions } from '@pages/Trade/TradingAccountInfo/components/TradingAccountInfoActions'
import { TradingAccountSelector } from '@pages/Trade/TradingAccountInfo/components/TradingAccountSelector'
import { TradingViewTradeButton } from '@pages/Trade/TradingAccountInfo/components/TradingViewTradeButton'
import { useFundAccount } from '@pages/Trade/TradingAccountInfo/hooks/useFundAccount'
import { Account } from '@pages/Trade/types'
import { mapAccountMetrics } from '@pages/Trade/utils/helpers'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'
import { CommonPaths } from '@utils/path-common'
import { TradingAccountRestrictions, isRestricted } from '@utils/trading-account.utils'
import { WalletRestrictions, isRestricted as isWalletRestricted } from '@utils/wallet.utils'

import { AccountGroupType } from '@model/AccountGroupType'
import { isTradingViewPlatformType } from '@model/PlatformTypeEnum'
import { isTradingAccountStatusCloseOnly } from '@model/TradingAccount'

import { useFundingNav } from '@hooks/useFundingNav'

import { Box } from '@shared/components/Box'

import { useDepositTracker } from '@analytics/amplitude/deposit'

import styles from './TradingAccountHeader.module.scss'

type TradingAccountHeaderProps = {
  account: Account
  onClick?: () => void
}

export const TradingAccountHeader = ({ account, onClick }: TradingAccountHeaderProps) => {
  const isMobile = useWindowResize()
  const navigate = useNavigate()
  const { navigateToFunding } = useFundingNav()
  const { t } = useTranslation()

  const isDemo = account.accountGroupType?.id === AccountGroupType.Demo

  const wsAccountKey = `default:${account.platformOverview.login}`
  const wsMetrics = useDxtradeAccountMetricsWS(wsAccountKey)
  const metrics = mapAccountMetrics({ ws: wsMetrics, rest: account.platformOverview })
  const isLowMargin = metrics.marginLevel !== null && metrics.marginLevel < 100
  const tracker = useDepositTracker()

  const apiClient = useApiClient(ClientApiClient)

  const walletId = 'wallet' in account ? account.wallet?.id : undefined
  const { data: walletData } = useQuery({
    queryKey: queryKeys.wallet.byId(walletId),
    queryFn: () => apiClient.getWalletById(walletId!),
    enabled: !!walletId,
  })

  const isTradingViewAccount = isTradingViewPlatformType(account.platformType?.id)
  const fundAccount = useFundAccount(account)

  const isCloseOnly = isTradingAccountStatusCloseOnly(account.status)

  const isTaTransferRestricted = isRestricted(
    TradingAccountRestrictions.TRANSFER_TO,
    'restrictions' in account ? account.restrictions : []
  )
  const isWalletTransferFromRestricted = isWalletRestricted(
    WalletRestrictions.TRANSFER_FROM,
    walletData?.restrictions
  )

  const transferRestrictedTooltip =
    isTaTransferRestricted || isWalletTransferFromRestricted || isCloseOnly
      ? t('This action is currently unavailable.')
      : undefined

  const isDepositRestricted = isWalletRestricted(
    WalletRestrictions.DEPOSIT,
    walletData?.restrictions
  )
  const depositRestrictedTooltip =
    isDepositRestricted || isCloseOnly
      ? t('Trading Account.Deposits for this trading account are unavailable')
      : undefined

  const makeTransfer = () => {
    const fromWalletId = 'wallet' in account ? account.wallet?.id : undefined
    const path = fromWalletId
      ? `/dashboard/traders-room/wallets/${fromWalletId}/transfer`
      : `/dashboard/traders-room/wallets/transfer`
    navigate(`${path}?toTradingAccountId=${account.id}`)
  }
  const makeDeposit = () => {
    tracker.start({ entry_point: 'trade_header', preset_ta_id: account.id })
    navigateToFunding(`${CommonPaths.TradersRoomTradingAccounts}/${account.id}/deposit`)
  }

  const addBalance = () => {
    navigate(`/dashboard/traders-room/trading-accounts/${account.id}/demo/add-balance`)
  }

  return isMobile ? (
    <div className={styles.accountInfoContainerMobileSticky}>
      {isLowMargin && <LowMarginAlert onDeposit={fundAccount} />}
      <div
        className={classNames(
          styles.accountInfoContainerMobile,
          isLowMargin && styles.accountInfoContainerMobileWithAlert
        )}
        onClick={onClick}
      >
        <div className={styles.tradingAccountInfoWrapperMobile}>
          <TradingAccountSelector account={account} />

          <SelectedTradingAccount account={account} />
        </div>
        <Box onClick={(e) => e.stopPropagation()}>
          <TradingAccountInfoActions
            isDemo={isDemo}
            onClickTransfer={makeTransfer}
            onClickDeposit={makeDeposit}
            addBalance={addBalance}
            account={account}
            isTradingViewAccount={isTradingViewAccount}
            transferRestrictedTooltip={transferRestrictedTooltip}
            depositRestrictedTooltip={depositRestrictedTooltip}
          />
        </Box>
      </div>
    </div>
  ) : (
    <div className={styles.accountInfoContainer}>
      <SelectedTradingAccount account={account} />
      <div className={styles.tradingAccountInfoWrapper}>
        <TradingAccountInfoActions
          isDemo={isDemo}
          onClickTransfer={makeTransfer}
          onClickDeposit={makeDeposit}
          addBalance={addBalance}
          account={account}
          isTradingViewAccount={isTradingViewAccount}
          transferRestrictedTooltip={transferRestrictedTooltip}
          depositRestrictedTooltip={depositRestrictedTooltip}
        />
        {isTradingViewAccount && <TradingViewTradeButton account={account} />}
        <TradingAccountSelector account={account} onClick={onClick} />
      </div>
    </div>
  )
}
